<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { computed, defineAsyncComponent, type Component } from 'vue'

import { usePipe } from '@render/composables/usePipe'
import {
  resolveCheckoutProvider,
  type CHECKOUT_VERSION_CONSTANT,
  type CheckoutProvider,
} from '@render/integrations/checkoutProvider'
import type { ComponentNode } from '@api/types.gen'

import type { CheckoutFields as Model } from './definition'

// One chunk per provider, so a built pipe fetches only the checkout its
// distribution renders.
const PROVIDER_COMPONENTS: Record<CheckoutProvider, Component> = {
  primer: defineAsyncComponent(() => import('../PrimerCheckout/PrimerCheckout.vue')),
  yuno: defineAsyncComponent(() => import('../YunoCheckout/YunoCheckout.vue')),
}

// A literal (not built from CHECKOUT_VERSION_CONSTANT) so build-platform-keys
// records the constant as required by this component; the type pins it.
const CHECKOUT_VERSION_KEY: `const.${typeof CHECKOUT_VERSION_CONSTANT}` =
  'const.global.checkoutVersion'

const fields = defineModel<Model>('fields', {
  default: () => ({}),
})
defineProps<{
  slotChildren?: Record<string, ComponentNode[]>
  editable?: boolean
}>()

const pipe = usePipe()

const provider = computed(() => resolveCheckoutProvider(pipe.getData(CHECKOUT_VERSION_KEY)))
</script>

<template>
  <!-- The provider checkout IS the root, so everything RenderComponents binds on
       this node falls through to it untouched: the event listeners (both
       providers emit the same five events), data-id (which RenderSlot resolves
       its slot owner from) and the class/style/animation hooks. Keyed so a
       change of the constant in the editor remounts instead of patching one SDK
       into the other. -->
  <component
    :is="PROVIDER_COMPONENTS[provider]"
    :key="provider"
    v-model:fields="fields"
    :slot-children="slotChildren"
    :editable="editable"
  />
</template>
